/**
 * WorkerFormPage — Renderiza cuestionario para que el trabajador lo complete.
 *
 * F7 Track A — flujo completo:
 *   1. Lee JWT magic-link (scope=magic_link_form_fill) desde sessionStorage (setToken en consume).
 *   2. Lee `questionnaire_code` desde query string (?questionnaire=epworth) o usa default.
 *   3. GET /api/forms/:code → FormDefinition
 *   4. POST /api/questionnaire-responses con status=in-progress al primer onChange (autosave).
 *   5. PATCH /api/questionnaire-responses/:id con cada onBlur (autosave).
 *   6. PATCH ... { status:'completed' } al hacer click "Finalizar" → backend computa score + retorna.
 *   7. Muestra resumen con score + color band.
 *
 * Mobile-first 375px viewport. Sin sessionStorage de PHI (solo JWT que ya viene del magic-link consume).
 *
 * PHI: el JWT magic-link tiene `sub=patient_id` pero el form NO muestra datos del paciente
 * — el trabajador SABE que es él porque accedió a su link personal.
 */
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { api, ApiError } from '@/lib/api'
import {
  DynamicForm,
  type FormDefinition,
  type QuestionnaireItem,
} from '@/lib/forms/dynamicRenderer'

interface PostResponse {
  id: string
  status: 'in-progress' | 'completed' | 'amended'
  score?: {
    total: number
    band_code: string
    band_display: string
    color: 'verde' | 'amarillo' | 'rojo' | 'negro'
  } | null
}

const COLOR_HEX: Record<string, string> = {
  verde: '#16A34A',
  amarillo: '#EAB308',
  rojo: '#DC2626',
  negro: '#0F172A',
}

export default function WorkerFormPage() {
  const [searchParams] = useSearchParams()
  // Sanitización defensiva: solo aceptamos slug ^[a-z0-9-]{1,64}$. Si llega texto basura
  // (p. ej. WhatsApp arrastró trailing text al detectar el link greedy) caemos a 'epworth'.
  const rawCode = searchParams.get('questionnaire')
  const code = rawCode && /^[a-z0-9-]{1,64}$/.test(rawCode) ? rawCode : 'epworth'
  const [items, setItems] = useState<QuestionnaireItem[]>([])
  const [responseId, setResponseId] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState<PostResponse | null>(null)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  // Estado terminal: el enlace/sesión expiró (guardado respondió 401). El magic link es
  // single-use y el JWT no se renueva, así que no hay recuperación in-place → pedir uno nuevo.
  const [linkExpired, setLinkExpired] = useState(false)

  const defQuery = useQuery({
    queryKey: ['form-definition', code],
    queryFn: async () => api.get<FormDefinition>(`/api/forms/${code}`, { anonymous: true }),
    staleTime: 5 * 60 * 1000,
  })

  // First-save mutation (creates the QR with status=in-progress)
  const createMutation = useMutation({
    mutationFn: async (initialItems: QuestionnaireItem[]) => {
      return api.post<PostResponse>('/api/questionnaire-responses', {
        questionnaire_code: code,
        status: 'in-progress',
        items: initialItems,
      })
    },
    onSuccess: (data) => {
      setResponseId(data.id)
      setSaveStatus('saved')
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401) setLinkExpired(true)
      else setSaveStatus('error')
    },
  })

  // Autosave on blur — PATCH existing QR
  const patchMutation = useMutation({
    mutationFn: async (vars: {
      id: string
      itemsToSave: QuestionnaireItem[]
      finalize?: boolean
    }) => {
      return api.patch<PostResponse>(`/api/questionnaire-responses/${vars.id}`, {
        items: vars.itemsToSave,
        ...(vars.finalize ? { status: 'completed' } : {}),
      })
    },
    onSuccess: (data, vars) => {
      setSaveStatus('saved')
      if (vars.finalize) {
        setSubmitted(data)
      }
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401) setLinkExpired(true)
      else setSaveStatus('error')
    },
  })

  function handleChange(next: QuestionnaireItem[]): void {
    setItems(next)
  }

  function handleBlur(next: QuestionnaireItem[]): void {
    setItems(next)
    setSaveStatus('saving')
    if (!responseId && !createMutation.isPending) {
      createMutation.mutate(next)
    } else if (responseId && !patchMutation.isPending) {
      patchMutation.mutate({ id: responseId, itemsToSave: next })
    }
  }

  function handleSubmit(): void {
    if (!responseId) {
      // No autosaved yet — create + submit in one go would be ideal but simpler to require autosave first
      setSaveStatus('saving')
      createMutation.mutate(items, {
        onSuccess: (created) => {
          patchMutation.mutate({ id: created.id, itemsToSave: items, finalize: true })
        },
      })
    } else {
      setSaveStatus('saving')
      patchMutation.mutate({ id: responseId, itemsToSave: items, finalize: true })
    }
  }

  useEffect(() => {
    // Auto-reset save status after 2s
    if (saveStatus === 'saved') {
      const t = setTimeout(() => setSaveStatus('idle'), 2000)
      return () => clearTimeout(t)
    }
  }, [saveStatus])

  if (defQuery.isPending) {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-slate-50 p-4">
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Cargando formulario…</span>
        </div>
      </main>
    )
  }

  if (defQuery.isError) {
    const err = defQuery.error
    const notFound = err instanceof ApiError && err.status === 404
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-slate-50 p-4 text-center">
        <AlertCircle className="h-12 w-12 text-red-500" />
        <h1 className="text-xl font-semibold text-slate-900">
          {notFound ? 'Formulario no disponible' : 'Error de conexión'}
        </h1>
        <p className="text-sm text-slate-600">
          {notFound
            ? `No encontramos el cuestionario "${code}". Verifica el link recibido.`
            : 'No pudimos cargar el formulario. Intenta nuevamente en unos minutos.'}
        </p>
      </main>
    )
  }

  if (linkExpired) {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center bg-slate-50 p-4 text-center">
        <div
          className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
          data-testid="form-link-expired"
          role="alert"
        >
          <AlertCircle className="mx-auto h-14 w-14 text-[color:var(--color-fitlab-red)]" />
          <h1 className="mt-4 text-xl font-semibold text-slate-900">Tu enlace expiró</h1>
          <p className="mt-2 text-sm text-slate-600">
            Por seguridad, el enlace para completar este formulario caducó. Solicita uno nuevo a
            FITLAB para continuar.
          </p>
        </div>
      </main>
    )
  }

  if (submitted && submitted.score) {
    const color = submitted.score.color
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center bg-slate-50 p-4 text-center">
        <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <CheckCircle2 className="mx-auto h-14 w-14 text-[color:var(--color-fitlab-green)]" />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">¡Gracias!</h1>
          <p className="mt-2 text-sm text-slate-600">
            Tu formulario fue enviado correctamente y el equipo médico FITLAB lo revisará.
          </p>
          <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">Puntaje</p>
            <p className="mt-1 text-3xl font-bold text-slate-900">{submitted.score.total}</p>
            <div
              className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold text-white"
              style={{ backgroundColor: COLOR_HEX[color] ?? '#64748B' }}
            >
              {submitted.score.band_display}
            </div>
          </div>
          <p className="mt-6 text-xs text-slate-500">
            Este resultado es preventivo y NO reemplaza una evaluación médica. FITLAB se pondrá en
            contacto contigo si requiere seguimiento.
          </p>
        </div>
      </main>
    )
  }

  if (submitted) {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center bg-slate-50 p-4 text-center">
        <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <CheckCircle2 className="mx-auto h-14 w-14 text-[color:var(--color-fitlab-green)]" />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">¡Enviado!</h1>
          <p className="mt-2 text-sm text-slate-600">
            Gracias por completar el formulario. FITLAB lo revisará pronto.
          </p>
        </div>
      </main>
    )
  }

  const isFinishing = patchMutation.isPending && (patchMutation.variables?.finalize ?? false)
  const requiredCount = defQuery.data.items.filter((i) => i.required && i.type !== 'display').length
  const answeredCount = items.filter((i) => {
    const a = i.answer[0]
    if (!a) return false
    return (
      a.valueInteger !== undefined ||
      a.valueBoolean !== undefined ||
      (a.valueString !== undefined && a.valueString.length > 0) ||
      a.valueDate !== undefined
    )
  }).length
  const allRequired = answeredCount >= requiredCount

  return (
    <main className="min-h-dvh bg-slate-50">
      <div className="mx-auto max-w-2xl px-4 py-6">
        <DynamicForm
          definition={defQuery.data}
          initialItems={items}
          onChange={handleChange}
          onBlur={handleBlur}
        />

        <div className="sticky bottom-0 mt-8 -mx-4 border-t border-slate-200 bg-white/90 px-4 py-3 backdrop-blur">
          <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
            <span>
              {saveStatus === 'saving' && 'Guardando…'}
              {saveStatus === 'saved' && '✓ Guardado'}
              {saveStatus === 'error' && '⚠ Error al guardar'}
              {saveStatus === 'idle' && ' '}
            </span>
            <span>
              {answeredCount} / {requiredCount}
            </span>
          </div>
          <button
            type="button"
            disabled={!allRequired || isFinishing}
            onClick={handleSubmit}
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-[color:var(--color-fitlab-blue)] px-6 text-base font-medium text-white shadow-sm transition-colors hover:bg-[color:var(--color-fitlab-blue-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isFinishing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Enviando…
              </>
            ) : (
              'Enviar'
            )}
          </button>
        </div>
      </div>
    </main>
  )
}
